/**
 * LeadReplyCard — wrapper que conecta hooks de leads al ReplyCard shared.
 * Sprint 7.6 Bloque A.
 */
import { useTranslation } from 'react-i18next';
import { ReplyCard } from '../outreach/ReplyCard.js';
import type { NormalizedReply, ReplyCardLabels } from '../outreach/types.js';
import { useSendReply, type OutreachReply } from '../../hooks/useLeads.js';

interface LeadReplyCardProps {
  reply: OutreachReply;
  leadId: string;
  onVerify: () => void;
  onDismiss: () => void;
}

export function LeadReplyCard({ reply, leadId, onVerify, onDismiss }: LeadReplyCardProps) {
  const { t } = useTranslation();
  const sendReply = useSendReply(leadId);

  const tsNum = reply.timestamp != null ? Number(reply.timestamp) : null;
  const normalized: NormalizedReply = {
    id: reply.id,
    fromAddress: reply.fromAddress,
    fromDomain: reply.fromDomain,
    toAddress: reply.toAddress,
    subject: reply.subject,
    replyText: reply.replyText,
    quoteText: reply.quoteText,
    timestamp: tsNum != null && !Number.isNaN(tsNum) ? tsNum : null,
    receivedAt: reply.receivedAt,
    isVerified: reply.isVerified,
    direction: reply.direction === 'outbound' ? 'outbound' : 'inbound',
  };

  const labels: ReplyCardLabels = {
    inbound: t('leads.outreach.replies.inbound', { defaultValue: 'Inbound' }),
    outbound: t('leads.outreach.replies.outbound'),
    possible: t('leads.outreach.replies.possible'),
    verified: t('leads.outreach.replies.verified'),
    confirm: t('leads.outreach.replies.confirm'),
    dismiss: t('leads.outreach.replies.dismiss'),
    from: t('leads.outreach.replies.from'),
    to: t('leads.outreach.replies.to'),
    subject: t('leads.outreach.replies.subject'),
    received: t('leads.outreach.replies.received'),
    sent: t('leads.outreach.replies.sent'),
    showQuote: t('leads.outreach.replies.showQuote'),
    hideQuote: t('leads.outreach.replies.hideQuote'),
    replyBtn: t('leads.outreach.replyBtn'),
    sendReplyBtn: t('composer.send'),
    sending: t('leads.outreach.sending'),
    cancel: t('common.cancel'),
    attachBtn: t('composer.attach.btn'),
    sizeExceeded: t('composer.attach.size_exceeded'),
    replyTo: t('leads.outreach.replyTo', { to: '{to}' }),
    composerPlaceholder: t('composer.editor.placeholder'),
    actionFailed: t('leads.outreach.action_failed'),
  };

  return (
    <ReplyCard
      reply={normalized}
      labels={labels}
      allowAttachments
      onVerify={onVerify}
      onDismiss={onDismiss}
      onReply={({ bodyHtml, bodyText, attachments }) =>
        sendReply.mutate({ replyId: reply.id, bodyHtml, bodyText, attachments: attachments ?? [] })
      }
      replyState={{ isPending: sendReply.isPending, isError: sendReply.isError, error: sendReply.error, reset: sendReply.reset }}
    />
  );
}
